import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Heart, DollarSign, Users, Target, Loader2, CheckCircle2, XCircle, Sparkles, Scissors, ChevronLeft, ChevronRight, Award, Play, CheckCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Donate() {
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [customAmount, setCustomAmount] = useState("");
  const [donorName, setDonorName] = useState("");
  const [donorEmail, setDonorEmail] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null); // null, 'checking', 'success', 'error'
  const [errorMessage, setErrorMessage] = useState("");
  
  // Team L.I.V.E donation state
  const [teamLiveAmount, setTeamLiveAmount] = useState("");
  const [teamLiveName, setTeamLiveName] = useState("");
  const [teamLiveEmail, setTeamLiveEmail] = useState("");
  const [isTeamLiveProcessing, setIsTeamLiveProcessing] = useState(false);
  
  // Moving Up In Style donation state
  const [movingUpAmount, setMovingUpAmount] = useState("");
  const [movingUpName, setMovingUpName] = useState("");
  const [movingUpEmail, setMovingUpEmail] = useState("");
  const [isMovingUpProcessing, setIsMovingUpProcessing] = useState(false);
  const [activeFlyer, setActiveFlyer] = useState(0);
  
  // Move, Heal, Fuel state
  const [moveHealFuelAmount, setMoveHealFuelAmount] = useState("");
  const [moveHealFuelName, setMoveHealFuelName] = useState("");
  const [moveHealFuelEmail, setMoveHealFuelEmail] = useState("");
  const [isMoveHealFuelProcessing, setIsMoveHealFuelProcessing] = useState(false);

  // Check if returning from Stripe
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    
    if (sessionId) {
      setPaymentStatus('checking');
      pollPaymentStatus(sessionId);
    }
  }, []);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setPaymentStatus('error');
      setErrorMessage('Payment status check timed out. Please check your email for confirmation.');
      return;
    }

    try {
      const response = await fetch(`${API}/payments/v1/checkout/status/${sessionId}`);
      if (!response.ok) {
        throw new Error('Failed to check payment status');
      }

      const data = await response.json();
      
      if (data.payment_status === 'paid') {
        setPaymentStatus('success');
        return;
      } else if (data.status === 'expired') {
        setPaymentStatus('error');
        setErrorMessage('Payment session expired. Please try again.');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setPaymentStatus('error');
      setErrorMessage('Error checking payment status. Please try again.');
    }
  };

  const handleDonate = async (amount) => {
    setErrorMessage("");
    
    // Validate amount
    const donationAmount = amount || parseFloat(customAmount);
    if (!donationAmount || donationAmount <= 0) {
      setErrorMessage('Please enter a valid amount greater than $0');
      return;
    }

    setIsProcessing(true);

    try {
      const originUrl = window.location.origin;
      
      const response = await fetch(`${API}/payments/v1/checkout/session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: donationAmount,
          donor_name: donorName || "Anonymous",
          donor_email: donorEmail || "",
          origin_url: originUrl
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create checkout session');
      }

      const data = await response.json();
      
      // Redirect to Stripe Checkout
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      setErrorMessage(error.message);
      setIsProcessing(false);
      console.error('Payment error:', error);
    }
  };

  // Handle Team L.I.V.E donation
  const handleTeamLiveDonate = async () => {
    setErrorMessage("");
    
    const donationAmount = parseFloat(teamLiveAmount);
    if (!donationAmount || donationAmount <= 0) {
      setErrorMessage('Please enter a valid amount greater than $0');
      return;
    }

    setIsTeamLiveProcessing(true);

    try {
      const originUrl = window.location.origin;
      
      const response = await fetch(`${API}/payments/v1/checkout/session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: donationAmount,
          donor_name: teamLiveName || "Anonymous",
          donor_email: teamLiveEmail || "",
          origin_url: originUrl,
          donation_type: "Team L.I.V.E"
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create checkout session');
      }

      const data = await response.json();
      
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      setErrorMessage(error.message);
      setIsTeamLiveProcessing(false);
      console.error('Team L.I.V.E Payment error:', error);
    }
  };

  // Handle Moving Up In Style donation
  const handleMovingUpDonate = async () => {
    setErrorMessage("");
    
    const donationAmount = parseFloat(movingUpAmount);
    if (!donationAmount || donationAmount <= 0) {
      setErrorMessage('Please enter a valid amount greater than $0');
      return;
    }

    setIsMovingUpProcessing(true);

    try {
      const originUrl = window.location.origin;
      
      const response = await fetch(`${API}/payments/v1/checkout/session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: donationAmount,
          donor_name: movingUpName || "Anonymous",
          donor_email: movingUpEmail || "",
          origin_url: originUrl,
          donation_type: "Moving Up In Style"
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create checkout session');
      }

      const data = await response.json();
      
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      setErrorMessage(error.message);
      setIsMovingUpProcessing(false);
      console.error('Moving Up In Style Payment error:', error);
    }
  };

  // Handle Move, Heal, Fuel donation
  const handleMoveHealFuelDonate = async () => {
    setErrorMessage("");
    
    const donationAmount = parseFloat(moveHealFuelAmount);
    if (!donationAmount || donationAmount <= 0) {
      setErrorMessage('Please enter a valid amount greater than $0');
      return;
    }

    setIsMoveHealFuelProcessing(true);

    try {
      const originUrl = window.location.origin;
      
      const response = await fetch(`${API}/payments/v1/checkout/session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: donationAmount,
          donor_name: moveHealFuelName || "Anonymous",
          donor_email: moveHealFuelEmail || "",
          origin_url: originUrl,
          donation_type: "Move, Heal, Fuel Scholarship"
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create checkout session');
      }

      const data = await response.json();
      
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      setErrorMessage(error.message);
      setIsMoveHealFuelProcessing(false);
      console.error('Move, Heal, Fuel Payment error:', error);
    }
  };

  const donationTiers = [
    {
      amount: "$50",
      title: "Workshop Supporter",
      description: "Provides supplies for one STEAM workshop",
      impact: "Hands-on learning materials for 10 students"
    },
    {
      amount: "$100",
      title: "Mental Health Advocate",
      description: "Funds mental health session for 5 students",
      impact: "Professional therapy and wellness support"
    },
    {
      amount: "$250",
      title: "Career Builder",
      description: "Trade certification scholarship",
      impact: "Full course materials and certification fees"
    },
    {
      amount: "$500",
      title: "Program Champion",
      description: "Sponsors a student for a full program cycle",
      impact: "Complete program participation and materials"
    },
    {
      amount: "$1,000",
      title: "Community Leader",
      description: "Supports multiple programs and initiatives",
      impact: "Direct impact on 25+ youth and families"
    },
    {
      amount: "Custom",
      title: "Your Choice",
      description: "Every dollar makes a difference",
      impact: "Any amount helps us serve more youth"
    }
  ];

  const waysToDonate = [
    {
      icon: <DollarSign className="w-8 h-8" />,
      title: "One-Time Donation",
      description: "Make an immediate impact with a single contribution"
    },
    {
      icon: <Heart className="w-8 h-8" />,
      title: "Monthly Giving",
      description: "Provide sustained support with recurring donations"
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Corporate Matching",
      description: "Double your impact through employer matching programs"
    },
    {
      icon: <Target className="w-8 h-8" />,
      title: "Sponsor a Program",
      description: "Fund an entire program or initiative"
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Header */}
      <section className="pt-32 pb-16 px-4 bg-gradient-to-br from-[#1167B1] to-[#0d5390] text-white">
        <div className="max-w-7xl mx-auto text-center">
          <Heart className="w-16 h-16 mx-auto mb-6" />
          <Badge className="bg-[#FFE102] text-[#17181B] font-bold mb-6">Make an Impact</Badge>
          <h1 className="text-5xl md:text-6xl font-bold mb-6">Support Our Mission</h1>
          <p className="text-xl text-white/90 max-w-4xl mx-auto">
            Your donation directly supports STEAM education, mental health services, and career development for Metro Atlanta youth. Together, we're building stronger futures.
          </p>
        </div>
      </section>

      {/* Move, Heal, Fuel - Upcoming Event */}
      <section id="move-heal-fuel" className="py-20 px-4 bg-gradient-to-br from-white via-gray-50 to-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-[#1167B1] text-white px-4 py-2 rounded-full font-bold mb-6">
              <Award className="w-5 h-5" />
              Upcoming Event
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-[#17181B] mb-4">Move, Heal, Fuel</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Pop-Up Scholarship Experience — Win $500 Scholarship!
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Event Flyer */}
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border-4 border-[#1167B1]">
                <img 
                  src="/events/move-heal-fuel.png"
                  alt="Move, Heal, Fuel Event Flyer"
                  className="w-full h-auto"
                />
              </div>
              
              {/* Event Highlights */}
              <div className="mt-6 grid grid-cols-2 gap-4">
                <div className="bg-[#1167B1]/10 rounded-lg p-4 text-center">
                  <p className="font-bold text-[#1167B1]">Saturday, June 13</p>
                  <p className="text-sm text-gray-600">12PM - 5PM</p>
                </div>
                <div className="bg-[#FFE102]/20 rounded-lg p-4 text-center">
                  <p className="font-bold text-[#17181B]">Win $500!</p>
                  <p className="text-sm text-gray-600">2 Winners</p>
                </div>
              </div>
            </div>

            {/* Donation Form */}
            <div>
              <Card className="border-2 border-[#1167B1] shadow-xl">
                <CardContent className="p-8">
                  <div className="mb-6">
                    <h3 className="text-2xl font-bold text-[#17181B] mb-3">Support This Event</h3>
                    <p className="text-gray-600 leading-relaxed">
                      Help us provide scholarships, healthy food, and wellness experiences for Metro Atlanta youth. Your donation supports fresh açaí bowls, massage demos, and an unforgettable day of healing and empowerment.
                    </p>
                  </div>

                  <div className="bg-[#FFE102]/20 border border-[#FFE102] rounded-lg p-4 mb-6">
                    <p className="text-[#17181B] font-semibold text-center">
                      <Award className="w-5 h-5 inline mr-2" />
                      Move with purpose. Heal with intention. Fuel your body.
                    </p>
                  </div>

                  {/* Quick Sponsor Buttons */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <Button
                      variant={moveHealFuelAmount === "25" ? "default" : "outline"}
                      className={`py-6 flex flex-col items-center ${
                        moveHealFuelAmount === "25"
                          ? "bg-[#1167B1] text-white hover:bg-[#0d5390]"
                          : "border-[#1167B1] text-[#1167B1] hover:bg-[#1167B1] hover:text-white"
                      }`}
                      onClick={() => setMoveHealFuelAmount("25")}
                      data-testid="move-heal-amount-25"
                    >
                      <span className="text-2xl font-bold">$25</span>
                      <span className="text-xs mt-1">Supporter</span>
                    </Button>
                    <Button
                      variant={moveHealFuelAmount === "50" ? "default" : "outline"}
                      className={`py-6 flex flex-col items-center ${
                        moveHealFuelAmount === "50"
                          ? "bg-[#1167B1] text-white hover:bg-[#0d5390]"
                          : "border-[#1167B1] text-[#1167B1] hover:bg-[#1167B1] hover:text-white"
                      }`}
                      onClick={() => setMoveHealFuelAmount("50")}
                      data-testid="move-heal-amount-50"
                    >
                      <span className="text-2xl font-bold">$50</span>
                      <span className="text-xs mt-1">Advocate</span>
                    </Button>
                    <Button
                      variant={moveHealFuelAmount === "100" ? "default" : "outline"}
                      className={`py-6 flex flex-col items-center ${
                        moveHealFuelAmount === "100"
                          ? "bg-[#1167B1] text-white hover:bg-[#0d5390]"
                          : "border-[#1167B1] text-[#1167B1] hover:bg-[#1167B1] hover:text-white"
                      }`}
                      onClick={() => setMoveHealFuelAmount("100")}
                      data-testid="move-heal-amount-100"
                    >
                      <span className="text-2xl font-bold">$100</span>
                      <span className="text-xs mt-1">Champion</span>
                    </Button>
                    <Button
                      variant={moveHealFuelAmount === "250" ? "default" : "outline"}
                      className={`py-6 flex flex-col items-center ${
                        moveHealFuelAmount === "250"
                          ? "bg-[#1167B1] text-white hover:bg-[#0d5390]"
                          : "border-[#1167B1] text-[#1167B1] hover:bg-[#1167B1] hover:text-white"
                      }`}
                      onClick={() => setMoveHealFuelAmount("250")}
                      data-testid="move-heal-amount-250"
                    >
                      <span className="text-2xl font-bold">$250</span>
                      <span className="text-xs mt-1">Scholarship Sponsor</span>
                    </Button>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="moveHealFuelAmount" className="text-sm font-semibold text-[#17181B] mb-2 block">
                        Custom Amount ($)
                      </Label>
                      <Input
                        id="moveHealFuelAmount"
                        type="number"
                        placeholder="Enter custom amount"
                        value={moveHealFuelAmount}
                        onChange={(e) => setMoveHealFuelAmount(e.target.value)}
                        className="text-lg p-3 border-gray-300 focus:border-[#1167B1]"
                        min="1"
                        step="0.01"
                        data-testid="move-heal-custom-amount"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="moveHealFuelName" className="text-sm font-semibold text-[#17181B] mb-2 block">
                          Your Name (Optional)
                        </Label>
                        <Input
                          id="moveHealFuelName"
                          type="text"
                          placeholder="Enter your name"
                          value={moveHealFuelName}
                          onChange={(e) => setMoveHealFuelName(e.target.value)}
                          className="p-3 border-gray-300 focus:border-[#1167B1]"
                          data-testid="move-heal-name"
                        />
                      </div>
                      <div>
                        <Label htmlFor="moveHealFuelEmail" className="text-sm font-semibold text-[#17181B] mb-2 block">
                          Email (Optional)
                        </Label>
                        <Input
                          id="moveHealFuelEmail"
                          type="email"
                          placeholder="Enter your email"
                          value={moveHealFuelEmail}
                          onChange={(e) => setMoveHealFuelEmail(e.target.value)}
                          className="p-3 border-gray-300 focus:border-[#1167B1]"
                          data-testid="move-heal-email"
                        />
                      </div>
                    </div>

                    <Button 
                      onClick={handleMoveHealFuelDonate}
                      className="w-full bg-[#1167B1] text-white hover:bg-[#0d5390] font-bold text-lg py-6 shadow-lg hover:shadow-xl transition-all"
                      disabled={isMoveHealFuelProcessing || !moveHealFuelAmount}
                      data-testid="move-heal-donate-btn"
                    >
                      {isMoveHealFuelProcessing ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin mr-2" />
                          Processing...
                        </>
                      ) : (
                        <>
                          Support This Event <Award className="ml-2 w-5 h-5" />
                        </>
                      )}
                    </Button>

                    <p className="text-sm text-gray-500 text-center">
                      Your support helps create transformative experiences for our youth!
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Event Features */}
              <div className="mt-6 grid grid-cols-3 gap-4 text-center">
                <div className="bg-gray-100 rounded-lg p-4">
                  <p className="font-bold text-[#17181B]">Fresh Food</p>
                  <p className="text-sm text-gray-600">Açaí bowls & healthy eats</p>
                </div>
                <div className="bg-gray-100 rounded-lg p-4">
                  <p className="font-bold text-[#17181B]">Wellness</p>
                  <p className="text-sm text-gray-600">Free massage demos</p>
                </div>
                <div className="bg-gray-100 rounded-lg p-4">
                  <p className="font-bold text-[#17181B]">Scholarships</p>
                  <p className="text-sm text-gray-600">$500 prizes</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team L.I.V.E Special Donation Section */}
      <section id="team-live" className="py-20 px-4 bg-gradient-to-br from-[#17181B] via-[#2a2b30] to-[#17181B] relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-32 h-32 bg-[#FFE102] rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-48 h-48 bg-[#1167B1] rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-5xl mx-auto relative z-10">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-[#FFE102] text-[#17181B] px-4 py-2 rounded-full font-bold mb-6">
              <Sparkles className="w-5 h-5" />
              Special Initiative
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Team L.I.V.E</h2>
            <p className="text-xl text-white/80 max-w-3xl mx-auto mb-2">
              <span className="text-[#FFE102] font-bold">L</span>ive • 
              <span className="text-[#FFE102] font-bold"> I</span>nvestment • 
              <span className="text-[#FFE102] font-bold"> V</span>alue • 
              <span className="text-[#FFE102] font-bold"> E</span>quity
            </p>
            <p className="text-lg text-white/70 max-w-2xl mx-auto">
              Join our mission to keep Nasim's legacy alive — investing in ourselves, while bringing value and equity to our communities.
            </p>
          </div>

          <Card className="bg-white/10 backdrop-blur-md border-2 border-[#FFE102]/50 shadow-2xl">
            <CardContent className="p-8 md:p-10">
              {/* Quick Amount Buttons */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                {[25, 50, 100, 250].map((amount) => (
                  <Button
                    key={amount}
                    variant={teamLiveAmount === amount.toString() ? "default" : "outline"}
                    className={`py-6 text-lg font-bold transition-all ${
                      teamLiveAmount === amount.toString()
                        ? "bg-[#FFE102] text-[#17181B] hover:bg-[#FFE102]/90 border-[#FFE102]"
                        : "bg-transparent text-white border-white/30 hover:bg-white/10 hover:border-[#FFE102]"
                    }`}
                    onClick={() => setTeamLiveAmount(amount.toString())}
                    data-testid={`team-live-amount-${amount}`}
                  >
                    ${amount}
                  </Button>
                ))}
              </div>

              <div className="space-y-6">
                <div>
                  <Label htmlFor="teamLiveAmount" className="text-base font-semibold text-white mb-2 block">
                    Custom Amount ($)
                  </Label>
                  <Input
                    id="teamLiveAmount"
                    type="number"
                    placeholder="Enter custom amount"
                    value={teamLiveAmount}
                    onChange={(e) => setTeamLiveAmount(e.target.value)}
                    className="text-lg p-4 bg-white/10 border-white/30 text-white placeholder:text-white/50 focus:border-[#FFE102]"
                    min="1"
                    step="0.01"
                    data-testid="team-live-custom-amount"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="teamLiveName" className="text-base font-semibold text-white mb-2 block">
                      Your Name (Optional)
                    </Label>
                    <Input
                      id="teamLiveName"
                      type="text"
                      placeholder="Enter your name"
                      value={teamLiveName}
                      onChange={(e) => setTeamLiveName(e.target.value)}
                      className="text-lg p-4 bg-white/10 border-white/30 text-white placeholder:text-white/50 focus:border-[#FFE102]"
                      data-testid="team-live-name"
                    />
                  </div>

                  <div>
                    <Label htmlFor="teamLiveEmail" className="text-base font-semibold text-white mb-2 block">
                      Email Address (Optional)
                    </Label>
                    <Input
                      id="teamLiveEmail"
                      type="email"
                      placeholder="Enter your email"
                      value={teamLiveEmail}
                      onChange={(e) => setTeamLiveEmail(e.target.value)}
                      className="text-lg p-4 bg-white/10 border-white/30 text-white placeholder:text-white/50 focus:border-[#FFE102]"
                      data-testid="team-live-email"
                    />
                  </div>
                </div>

                <Button 
                  onClick={handleTeamLiveDonate}
                  className="w-full bg-[#FFE102] text-[#17181B] hover:bg-[#FFE102]/90 font-bold text-xl py-7 shadow-lg hover:shadow-xl transition-all"
                  disabled={isTeamLiveProcessing || !teamLiveAmount}
                  data-testid="team-live-donate-btn"
                >
                  {isTeamLiveProcessing ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      Processing...
                    </>
                  ) : (
                    <>
                      Donate to Team L.I.V.E <Sparkles className="ml-2 w-5 h-5" />
                    </>
                  )}
                </Button>

                <p className="text-sm text-white/60 text-center">
                  100% of Team L.I.V.E donations go directly to youth leadership programs
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Donation Tiers */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-[#1167B1] text-white mb-4">Donation Levels</Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-[#17181B] mb-4">Every Gift Makes a Difference</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">Choose a donation level that works for you. All contributions are tax-deductible.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {donationTiers.map((tier, index) => (
              <Card key={index} className="border-2 hover:border-[#1167B1] transition-all hover:shadow-xl">
                <CardContent className="p-8 text-center">
                  <div className="text-4xl font-bold text-[#1167B1] mb-2">{tier.amount}</div>
                  <h3 className="text-xl font-bold text-[#17181B] mb-2">{tier.title}</h3>
                  <p className="text-gray-600 mb-4">{tier.description}</p>
                  <div className="pt-4 border-t border-gray-200">
                    <p className="text-sm text-gray-500 mb-4">Impact:</p>
                    <p className="text-sm font-semibold text-[#17181B]">{tier.impact}</p>
                  </div>
                  <Button 
                    className="w-full mt-6 bg-[#FFE102] text-[#17181B] hover:bg-[#FFE102]/90 font-bold"
                    onClick={() => {
                      if (tier.amount === "Custom") {
                        document.getElementById('custom-donation')?.scrollIntoView({ behavior: 'smooth' });
                      } else {
                        const amount = parseFloat(tier.amount.replace(/[$,]/g, ''));
                        handleDonate(amount);
                      }
                    }}
                    disabled={isProcessing}
                  >
                    {isProcessing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Donate {tier.amount}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Custom Donation Form */}
      <section id="custom-donation" className="py-20 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="bg-[#1167B1] text-white mb-4">Custom Amount</Badge>
            <h2 className="text-4xl font-bold text-[#17181B] mb-4">Make a Custom Donation</h2>
            <p className="text-xl text-gray-600">Enter your preferred amount and donor information</p>
          </div>

          <Card className="shadow-xl border-2 border-[#1167B1]">
            <CardContent className="p-8">
              {/* Payment Status Messages */}
              {paymentStatus === 'checking' && (
                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-center">
                  <Loader2 className="w-5 h-5 animate-spin text-blue-600 mr-3" />
                  <span className="text-blue-800">Checking payment status...</span>
                </div>
              )}

              {paymentStatus === 'success' && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mr-3" />
                  <span className="text-green-800">Thank you! Your donation was successful.</span>
                </div>
              )}

              {paymentStatus === 'error' && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center">
                  <XCircle className="w-5 h-5 text-red-600 mr-3" />
                  <span className="text-red-800">{errorMessage}</span>
                </div>
              )}

              {errorMessage && paymentStatus !== 'error' && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <span className="text-red-800">{errorMessage}</span>
                </div>
              )}

              <div className="space-y-6">
                <div>
                  <Label htmlFor="customAmount" className="text-base font-semibold text-[#17181B] mb-2 block">
                    Donation Amount ($) *
                  </Label>
                  <Input
                    id="customAmount"
                    type="number"
                    placeholder="Enter amount"
                    value={customAmount}
                    onChange={(e) => setCustomAmount(e.target.value)}
                    className="text-lg p-4"
                    min="1"
                    step="0.01"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="donorName" className="text-base font-semibold text-[#17181B] mb-2 block">
                      Your Name (Optional)
                    </Label>
                    <Input
                      id="donorName"
                      type="text"
                      placeholder="Enter your name"
                      value={donorName}
                      onChange={(e) => setDonorName(e.target.value)}
                      className="text-lg p-4"
                    />
                  </div>

                  <div>
                    <Label htmlFor="donorEmail" className="text-base font-semibold text-[#17181B] mb-2 block">
                      Email Address (Optional)
                    </Label>
                    <Input
                      id="donorEmail"
                      type="email"
                      placeholder="Enter your email"
                      value={donorEmail}
                      onChange={(e) => setDonorEmail(e.target.value)}
                      className="text-lg p-4"
                    />
                  </div>
                </div>

                <Button 
                  onClick={() => handleDonate(null)}
                  className="w-full bg-[#1167B1] text-white hover:bg-[#0d5390] font-bold text-xl py-7"
                  disabled={isProcessing}
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      Processing...
                    </>
                  ) : (
                    <>
                      Complete Donation <Heart className="ml-2" />
                    </>
                  )}
                </Button>

                <p className="text-sm text-gray-600 text-center">
                  Secure payment processing powered by Stripe
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Ways to Donate */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-[#FFE102] text-[#17181B] font-bold mb-4">Giving Options</Badge>
            <h2 className="text-4xl font-bold text-[#17181B] mb-4">Ways to Give</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">Choose the giving method that works best for you.</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {waysToDonate.map((way, index) => (
              <Card key={index} className="text-center hover:shadow-lg transition-all">
                <CardContent className="p-6">
                  <div className="w-16 h-16 bg-[#1167B1] text-white rounded-full flex items-center justify-center mx-auto mb-4">
                    {way.icon}
                  </div>
                  <h3 className="text-xl font-bold text-[#17181B] mb-2">{way.title}</h3>
                  <p className="text-gray-600 text-sm">{way.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Impact Story */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <Card className="border-2 border-[#1167B1] shadow-2xl">
            <CardContent className="p-8 md:p-12">
              <div className="text-center mb-8">
                <Badge className="bg-[#FFE102] text-[#17181B] font-bold mb-4">Our Commitment</Badge>
                <h2 className="text-3xl font-bold text-[#17181B] mb-4">Where Your Donation Goes</h2>
              </div>
              
              <div className="space-y-6">
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-bold text-[#17181B]">Direct Program Support</span>
                    <span className="text-[#1167B1] font-bold">75%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div className="bg-[#1167B1] h-3 rounded-full" style={{ width: '75%' }}></div>
                  </div>
                  <p className="text-sm text-gray-600 mt-2">STEAM education, mental health services, trade certifications</p>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-bold text-[#17181B]">Staff & Operations</span>
                    <span className="text-[#1167B1] font-bold">15%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div className="bg-[#1167B1] h-3 rounded-full" style={{ width: '15%' }}></div>
                  </div>
                  <p className="text-sm text-gray-600 mt-2">Qualified staff, program coordinators, administrative support</p>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-bold text-[#17181B]">Outreach & Growth</span>
                    <span className="text-[#1167B1] font-bold">10%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div className="bg-[#1167B1] h-3 rounded-full" style={{ width: '10%' }}></div>
                  </div>
                  <p className="text-sm text-gray-600 mt-2">Community outreach, program expansion, partnership development</p>
                </div>
              </div>

              <div className="mt-8 pt-8 border-t border-gray-200 text-center">
                <p className="text-gray-700 mb-4">
                  <span className="font-bold text-[#17181B]">100% of your donation</span> reinvests directly back into the youth we serve through programs, services, and community initiatives.
                </p>
                <p className="text-sm text-gray-600">Calvin D. Butts Inc. is a 501(c)(3) nonprofit organization. Your donation is tax-deductible to the full extent of the law.</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Moving Up In Style - Event Recap */}
      <section id="moving-up-recap" className="py-20 px-4 bg-gradient-to-br from-[#17181B] via-[#2a2b30] to-[#17181B] relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-20 right-20 w-64 h-64 bg-[#FFE102] rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 left-20 w-48 h-48 bg-[#1167B1] rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-[#FFE102] text-[#17181B] px-4 py-2 rounded-full font-bold mb-6">
              <CheckCircle className="w-5 h-5" />
              Event Complete
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Moving Up In Style</h2>
            <p className="text-xl text-white/80 max-w-3xl mx-auto">
              Thank You to Our Supporters & Donors!
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Recap Video */}
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border-4 border-[#FFE102]">
                <video 
                  controls
                  className="w-full h-auto"
                  poster="/events/moving-up-style-1.png"
                >
                  <source src="/events/moving-up-recap.mov" type="video/quicktime" />
                  <source src="/events/moving-up-recap.mov" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
              <div className="absolute -bottom-4 -right-4 bg-[#FFE102] text-[#17181B] px-4 py-2 rounded-full font-bold text-sm shadow-lg">
                <Play className="w-4 h-4 inline mr-1" /> Watch Recap
              </div>
            </div>

            {/* Thank You Message */}
            <div>
              <Card className="bg-white/10 backdrop-blur-md border-2 border-[#FFE102]/50 shadow-2xl">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-[#FFE102] rounded-full flex items-center justify-center">
                      <Heart className="w-6 h-6 text-[#17181B]" />
                    </div>
                    <h3 className="text-2xl font-bold text-white">A Heartfelt Thank You</h3>
                  </div>
                  
                  <p className="text-white/90 leading-relaxed mb-6 text-lg">
                    Thanks to your generous support, we were able to provide <span className="text-[#FFE102] font-bold">haircuts, hairstyles, facials, manicures, and lunch</span> to <span className="text-[#FFE102] font-bold">20+ scholars</span> from 7th-11th grade!
                  </p>

                  <div className="bg-white/10 rounded-lg p-4 mb-6">
                    <p className="text-white/80 text-sm font-semibold mb-3">Special Thanks To:</p>
                    <ul className="space-y-2">
                      <li className="flex items-start gap-2 text-white/90">
                        <CheckCircle className="w-5 h-5 text-[#FFE102] flex-shrink-0 mt-0.5" />
                        <span><strong>Frederick Douglass High School</strong> & Director Jasmine Shepard</span>
                      </li>
                      <li className="flex items-start gap-2 text-white/90">
                        <CheckCircle className="w-5 h-5 text-[#FFE102] flex-shrink-0 mt-0.5" />
                        <span><strong>Mother Hubbard's Daycare & Kindergarten, Inc.</strong> for providing transportation</span>
                      </li>
                      <li className="flex items-start gap-2 text-white/90">
                        <CheckCircle className="w-5 h-5 text-[#FFE102] flex-shrink-0 mt-0.5" />
                        <span><strong>Atlanta Heights Charter School</strong></span>
                      </li>
                    </ul>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="bg-[#FFE102]/20 rounded-lg p-4 text-center">
                      <p className="text-3xl font-bold text-[#FFE102]">20+</p>
                      <p className="text-white/70 text-sm">Scholars Served</p>
                    </div>
                    <div className="bg-[#FFE102]/20 rounded-lg p-4 text-center">
                      <p className="text-3xl font-bold text-[#FFE102]">7th-11th</p>
                      <p className="text-white/70 text-sm">Grade Students</p>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 justify-center">
                    <span className="bg-white/20 text-white px-3 py-1 rounded-full text-sm">Haircuts</span>
                    <span className="bg-white/20 text-white px-3 py-1 rounded-full text-sm">Hairstyles</span>
                    <span className="bg-white/20 text-white px-3 py-1 rounded-full text-sm">Facials</span>
                    <span className="bg-white/20 text-white px-3 py-1 rounded-full text-sm">Manicures</span>
                    <span className="bg-white/20 text-white px-3 py-1 rounded-full text-sm">Lunch</span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Flyer Gallery */}
          <div className="mt-12 grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            <div className="rounded-xl overflow-hidden shadow-lg border-2 border-white/20">
              <img 
                src="/events/moving-up-style-1.png" 
                alt="Moving Up In Style Flyer 1"
                className="w-full h-auto"
              />
            </div>
            <div className="rounded-xl overflow-hidden shadow-lg border-2 border-white/20">
              <img 
                src="/events/moving-up-style-2.png" 
                alt="Moving Up In Style Flyer 2"
                className="w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Donate;
